import { renderIf } from 'deckard-ui/dist/components/RenderIf/RenderIf';
import isEmpty from 'lodash/isEmpty';
import { useMemo } from 'react';
import styled from 'styled-components';
import { DATA_STATUS } from '../../../constants';
import { SolidSkeletonSection } from '../../../placeholders/skeleton-placeholders/generic-skeleton-placeholders';
import { TMultiLicenseDataDisplay } from './multi-license-data-display-types';
import { multiLicenseFlowProcessor } from './post-processors';

interface IGrid {
  isEmpty: boolean;
}

const Grid = styled.div<IGrid>`
  width: 100%;
  display: ${(props: IGrid) => (props.isEmpty ? 'none' : 'grid')};
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
`;

const Row = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;

  p {
    margin: 0;

    :first-child {
      font-weight: bold;
    }
  }
`;

function MultiLicenseDataDisplayComponent(props: TMultiLicenseDataDisplay) {
  const { dataStore, multiLicenseDisplay } = props;

  const dataGetter = useMemo(
    () => multiLicenseFlowProcessor(dataStore),
    [dataStore]
  );

  const pulledMultiLicenseData = useMemo(() => {
    // The value returned here doesn't matter as the loading skeleton is on the screen when the data is inflight.
    if (dataStore === 'IN_PROGRESS') {
      return [];
    }

    const { items = [] } = multiLicenseDisplay || {};

    return items.map((item) => ({
      key: item.path,
      label: item.label,
      value: dataGetter(item),
    }));
  }, [dataStore, multiLicenseDisplay, dataGetter]);

  if (dataStore === DATA_STATUS.inProgress) {
    return (
      <SolidSkeletonSection
        ariaLabel="Loading skeleton for property information"
        dataTestid="multi-license-data-display-skeleton"
        height={30}
        margin="0"
      />
    );
  }

  return (
    <Grid
      isEmpty={isEmpty(pulledMultiLicenseData)}
      data-testid="multi-license-data-display-grid"
    >
      {pulledMultiLicenseData?.map(({ key, label, value }) => (
        <Row key={key}>
          <p>{label}</p>
          <p>{value}</p>
        </Row>
      ))}
    </Grid>
  );
}

function isEnabled(props: TMultiLicenseDataDisplay) {
  return Boolean(props?.multiLicenseDisplay?.enabled);
}

export const MultiLicenseDataDisplay = renderIf(
  isEnabled,
  MultiLicenseDataDisplayComponent
);
